import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Loading } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import Form from "../carbon/Form/Form";
import BankingRow from "./BankingRow";
import {
  BankingInformationFormFields,
  BankingInfoFormFields,
  bankingInformationValidationSchema,
  buildBankingInfoFormRow,
  buildBankingInfoRequest,
} from "./bankingInformationValidationSchema";
import {
  CompanyProfileRequest,
  CompanyProfileResponse,
  CountryResponse,
} from "../../types";
import { useUpdateCompanyProfile } from "../../hooks/useCompany";
import { useUser } from "../../context";
import "./bankingInformationForm.scss";

interface BankingInformationFormProps {
  profile: CompanyProfileResponse;
  countries: CountryResponse[];
  setIsDirty: (isDirty: boolean) => void;
}

const emptyBankingInfo: BankingInfoFormFields = {
  bankName: "",
  accountNumber: "",
  accountType: "",
  currency: "",
  swift: "",
  iban: "",
  routingNumber: "",
  countryId: "",
};

/**
 * The Banking Information wizard sub-form (`PUT /company/{id}/profile`), rebuilt on Carbon +
 * React Hook Form's `useFieldArray` (Task 9, Profile/Company flow) in place of 395 lines of
 * Tailwind and a two-stage "stage a draft locally, then merge with the already-saved list on
 * Save" flow — the SAME rewrite `AddressesForm.tsx` (Task 8) already did for addresses, mirrored
 * here verbatim (see that file's own docstring for the full rationale): ONE `bankingInfos` array,
 * seeded from `profile.bankingInfos`, editable in place via `useFieldArray` (`append`/`remove`),
 * submitted whole. Real behaviour change from the pre-Carbon form (which only ever let you ADD
 * new rows or remove an unsaved one), safe for the identical reason Task 8 already established:
 * `CompanyService#updateCompanyBankingInfos` (backend) already fully REPLACES the list on every
 * save (`company.getBankingInfos().clear()` then re-adds from the request) — the pre-Carbon
 * form's "existing rows are read-only" behaviour was a FRONTEND-only restriction with no backend
 * counterpart.
 *
 * `onSubmit` spreads `{...profile, ...}` (not just `{bankingInfos}`), same as every other wizard
 * sub-form — `taxId` is `@NotBlank` on the backend (Task 2), and every independent save must keep
 * carrying it.
 *
 * Data-loss fix: `routingNumber`/`countryId` are real `BankingInfoRequest`/`BankingInfoRef`
 * fields the backend has always supported, but the original Task 9 rebuild's row seeding and
 * submit mapping both omitted them — because `updateCompanyBankingInfos` clears and rebuilds the
 * whole list on every save, saving through this form silently nulled out any row's already-stored
 * `routingNumber`/`countryId`. Both are now seeded (`buildBankingInfoFormRow`), editable
 * (`BankingRow`'s `routingNumber` `TextField` / `countryId` `SelectField`, fed by the same
 * `countries` prop `AddressesForm` already threads from `usePublicCountries`), and round-tripped
 * on submit (`buildBankingInfoRequest`) — see `bankingInformationValidationSchema.ts` for both
 * pure mapping functions and their own unit tests.
 */
const BankingInformationForm = ({
  profile,
  countries,
  setIsDirty,
}: BankingInformationFormProps) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const defaultValues: BankingInformationFormFields = useMemo(
    () => ({
      bankingInfos: (profile.bankingInfos || []).map(buildBankingInfoFormRow),
    }),
    [profile]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<BankingInformationFormFields>({
    resolver: yupResolver(bankingInformationValidationSchema),
    defaultValues,
  });

  // Same re-sync + one-directional dirty-forwarding precedent as AddressesForm.tsx/
  // BasicInformationForm.tsx — see those files' own docstrings/comments for the full rationale.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (isDirty) {
      setIsDirty(true);
    }
  }, [isDirty, setIsDirty]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "bankingInfos",
  });

  const { mutate: updateProfile, isPending: isSaving } =
    useUpdateCompanyProfile(() => {
      setIsDirty(false);
    });

  const onSubmit = (data: BankingInformationFormFields) => {
    updateProfile({
      ...profile,
      companyId: user?.company?.id,
      bankingInfos: data.bankingInfos.map(buildBankingInfoRequest),
    } as CompanyProfileRequest);
  };

  return (
    <div className="banking-information-form">
      <div className="banking-information-form__header">
        <h3>{t("companyProfile.bankingInformation")}</h3>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Add}
          type="button"
          onClick={() => append(emptyBankingInfo)}
        >
          {t("companyProfile.addBankingInformation")}
        </Button>
      </div>

      {isSaving ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          noValidate
          className="banking-information-form__form"
        >
          {fields.length > 0 && (
            <div className="banking-information-form__rows">
              {fields.map((field, index) => (
                <BankingRow
                  key={field.id}
                  index={index}
                  control={control}
                  countries={countries}
                  onRemove={() => remove(index)}
                />
              ))}
            </div>
          )}

          <div className="banking-information-form__actions">
            <Button type="submit" disabled={isSaving || !isDirty}>
              {t("cta.save")}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default BankingInformationForm;
